/**
 * DCMS Modal Loader — animated background shown while a window, a side panel or a list is loading.
 *
 * Surfaces:
 *  - modal — a window with a frame (iziModal option `iframe`) carries the class `iziModal-content-loader`
 *    on `.iziModal-content` from opening until the `load` event of the frame (iziModal.js, open()). The top
 *    DCMS2 window — the one Modals opens every window in (Modals.findHighestDCMS2Parent()) — watches that
 *    class and lays the layer over the frame for that time. Frames created by Modals itself
 *    (showFullScreenPopup) join through watchFrame();
 *  - panel — the side panel of a list row (dcms-row-panel.js) calls watchFrame(frame, { surface: 'panel' });
 *  - list  — a list refreshed with AJAX (search, sorting, filters, pagination: dcmsAjaxShowLoading() in
 *    views/Recordset/list_head.tpl) or the results frame of views/Recordset/search.tpl calls
 *    show(host, { surface: 'list' }) and hide() when the list has loaded;
 *  - page  — the page loading overlay of dcms-loader.js (#dcmsPageLoadingOverlay: a page of the content
 *    frame loading, e.g. a list opened from the menu) calls show(overlay, { surface: 'page' }).
 *
 * The canvas is painted by an EFFECT — a module registered under a name. The core owns everything the
 * effects share:
 *  - timing: the layer appears after showDelayMs (fast loads never flash it), stays at least minShowMs,
 *    fades out when loading ends, disappears at once when the window closes, and after maxMs at the latest,
 *  - the layer (`.dcms-mload`), the theme colours (light / dark theme × tone, CSS variables in
 *    assets/css/modal-loader.css), the caption in the middle, prefers-reduced-motion,
 *  - the frame loop (a timer between frames, requestAnimationFrame for the frame itself, at the fps of the
 *    effect; nothing is painted in a hidden tab or while the layer is off screen)
 *    and the canvas size (device pixels capped at MAX_CANVAS_PIXELS).
 *
 * Configuration: DCMS.ini section [modalLoader] (defaults: config/DCMS.defaults.ini), rendered by
 * views/DCMS/main.tpl as <html data-dcms-modal-loader='{json}'> (AMPD\DCMS2\UI\ModalLoader\ModalLoaderConfig).
 * The keys set the window; `surfaces.panel` / `.list` / `.page` override them for the other surfaces;
 * `effectOptions` holds the options of every effect (rain.glyphs = … → { rain: { glyphs: … } }); `user` (the
 * user's appearance preferences: effect portal | off | name, intensity strong | subtle) applies over every surface
 * that has an effect — off = no layer (the default: each user turns the background on), a name = that effect,
 * subtle = intensity whisper without a caption.
 * effect = "none" turns a surface off (the window keeps the spinner of iziModal, the list its own).
 *
 * Public API — DCMS2.modalLoader (every DCMS2 document has its own; the windows are watched by the top one):
 *   register(name, effect)            adds an effect (contract below)
 *   has(name)                         whether an effect is registered
 *   configure(options)                overrides the configuration for this page (keys as in the JSON)
 *   watchFrame(iframe, options)       layer over a frame until it has loaded; options: surface ('modal' |
 *                                     'panel'), until ('load' | 'interactive' — ends when the new page is
 *                                     parsed, not when every image has arrived)
 *   show(host, options)               layer in a positioned element until hide(); options: surface
 *                                     → { hide() }
 *   preview(host, options)            endless layer (documentation, demos); options: configuration keys,
 *                                     surface, theme ('light' | 'dark') → { destroy() }
 *
 * Effect contract (docs: 03-dcms2/ui-components/modal-loader.md):
 *   DCMS2.modalLoader.register('name', {
 *       fps: 24,                 // optional frame-rate cap, 1–60 (default 24)
 *       create(ctx) {            // called when the layer is shown; returns the instance
 *           return {
 *               frame(now, dt) {},   // draws one frame — clears the canvas itself
 *               resize() {},         // optional: ctx.width / ctx.height / ctx.dpr changed
 *               still() {},          // optional: one static frame for reduced motion (default: frame(0, 0))
 *               destroy() {}         // optional: releases resources
 *           };
 *       }
 *   });
 *   ctx: canvas, context (2D, scaled to device pixels — draw in CSS px), width, height (CSS px — the visible
 *   part of the layer: a tall list gets a canvas of the screen's size, moved as the page scrolls), dpr,
 *   options (effect options from DCMS.ini, strings), intensity ('whisper' | 'soft' | 'bold'), surface,
 *   colors() → { fg, head, bg, gain, glow, key } (key changes when any colour changes), reducedMotion.
 *   Rules: draw only on ctx.canvas (detached helper canvases are fine), take colours only from
 *   ctx.colors(), no own timers / requestAnimationFrame / listeners / DOM changes, keep a frame cheap
 *   (cache what can be cached). An exception thrown by the effect removes the layer.
 *
 * @author AMPD Sp. z o.o.
 * @version 1.0
 */

import { rainEffect } from './modal-loader/rain.js';

/** Window configuration — same defaults as config/DCMS.defaults.ini and ModalLoaderConfig */
const DEFAULTS = {
    effect: 'rain',
    tone: 'green',
    intensity: 'bold',
    caption: 'decode',
    background: 'window',
    showDelayMs: 150,
    minShowMs: 450,
    maxMs: 30000,
    script: '',
    effectOptions: { rain: { glyphs: 'katakana' } },
    // the user's preferences — off until the user turns the background on (appearance preferences)
    user: { effect: 'off', intensity: 'strong' },
    surfaces: {
        list: { intensity: 'soft', caption: 'none', background: 'transparent', minShowMs: 0 },
        page: { intensity: 'soft', caption: 'none', background: 'transparent', minShowMs: 0 }
    }
};

const SURFACES = ['modal', 'panel', 'list', 'page'];
/** Keys a surface may override */
const SURFACE_KEYS = ['effect', 'tone', 'intensity', 'caption', 'background', 'showDelayMs', 'minShowMs', 'maxMs'];
const TONES = ['neutral', 'green', 'accent'];
const INTENSITIES = ['whisper', 'soft', 'bold'];
const CAPTIONS = ['decode', 'cursor', 'none'];
const BACKGROUNDS = ['window', 'transparent'];
const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

/** Device pixels of one canvas at most — a full-screen window on a 4K screen would otherwise mean ~33 Mpx */
const MAX_CANVAS_PIXELS = 3000000;

/** Characters of the "decoding" caption (case follows the text) — fixed width, so the caption does not jitter */
const SCRAMBLE_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const SCRAMBLE_LOWER = 'abcdefghijklmnopqrstuvwxyz0123456789';

/** Just over the opacity transition of .dcms-mload (0.32 s) */
const FADE_OUT_MS = 340;

function pick(set) {
    return set.charAt(Math.floor(Math.random() * set.length));
}

function clampInt(value, min, max, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function translate(key, fallback) {
    return typeof window.__i18n === 'function' ? window.__i18n(key, 'commonjs', fallback) : fallback;
}

/** The window Modals opens windows in — same walk as Modals.findHighestDCMS2Parent() */
function dcmsTopWindow() {
    let current = window;
    let highest = window;
    try {
        while (current !== window.top) {
            current = current.parent;
            if (current.DCMS2) highest = current;
        }
    } catch (e) {
        // cross-origin parent — the highest reachable DCMS2 window stays
    }
    return highest;
}

/* ── Caption: text "decoded" letter by letter ───────────────────────────── */

class Decoder {
    static START_MS = 200;
    static PER_CHAR_MS = 55;
    static HOLD_MS = 2600;

    constructor(el, text, loop) {
        this.el = el;
        this.text = text;
        this.loop = loop;
        this.t0 = -1;
        this.done = false;
    }

    tick(now) {
        if (this.t0 < 0) this.t0 = now;
        const n = this.text.length;
        let elapsed = now - this.t0;
        if (this.loop && elapsed > Decoder.START_MS + n * Decoder.PER_CHAR_MS + Decoder.HOLD_MS) {
            // preview: the caption encrypts itself again
            this.t0 = now;
            elapsed = 0;
            this.done = false;
        }
        if (this.done) return;
        const revealed = Math.max(0, Math.min(n, Math.floor((elapsed - Decoder.START_MS) / Decoder.PER_CHAR_MS)));
        let out = this.text.slice(0, revealed);
        for (let i = revealed; i < n; i++) {
            const ch = this.text.charAt(i);
            const lower = ch !== ch.toUpperCase();
            out += ch === ' ' ? ' ' : pick(lower ? SCRAMBLE_LOWER : SCRAMBLE_UPPER);
        }
        this.el.textContent = out;
        this.done = revealed === n;
    }

    finish() {
        this.el.textContent = this.text;
        this.done = true;
    }
}

/* ── Layer: canvas + caption, driven by one effect instance ─────────────── */

class Layer {
    /**
     * @param {Document} doc       document of the host element (the layer, its timers and frames live there)
     * @param {Object} config      normalized configuration of the surface
     * @param {Object} effect      registered effect
     * @param {boolean} preview    endless preview (looping caption, no status for screen readers)
     */
    constructor(doc, config, effect, preview) {
        this.win = doc.defaultView;
        this.config = config;
        this.effect = effect;
        this.instance = null;
        this.raf = 0;
        this.wake = 0;
        this.timer = 0;
        this.last = 0;
        this.frames = 0;
        this.colorsCache = null;
        this.onFrame = null;
        this.onError = null;
        this.reducedMotion = !!(this.win.matchMedia && this.win.matchMedia('(prefers-reduced-motion: reduce)').matches);

        this.root = doc.createElement('div');
        this.root.className = 'dcms-mload dcms-mload--tone-' + config.tone + ' dcms-mload--' + config.surface
            + ' dcms-mload--effect-' + config.effect + (config.background === 'transparent' ? ' dcms-mload--transparent' : '');

        this.canvas = doc.createElement('canvas');
        this.canvas.className = 'dcms-mload-canvas';
        this.canvas.setAttribute('aria-hidden', 'true');
        this.root.appendChild(this.canvas);

        if (!preview) {
            const status = doc.createElement('span');
            status.className = 'dcms-mload-sr';
            status.setAttribute('role', 'status');
            status.textContent = translate('modalLoaderStatus', 'Loading…');
            this.root.appendChild(status);
        }

        this.decoder = null;
        this.captionEl = null;
        this.bandTop = -1;
        if (config.caption !== 'none') {
            const caption = doc.createElement('div');
            this.captionEl = caption;
            caption.className = 'dcms-mload-caption';
            caption.setAttribute('aria-hidden', 'true');
            if (config.caption === 'decode') {
                const text = doc.createElement('span');
                text.className = 'dcms-mload-text';
                caption.appendChild(text);
                this.decoder = new Decoder(text, translate('modalLoaderCaption', 'loading'), preview);
            }
            const cursor = doc.createElement('span');
            cursor.className = 'dcms-mload-cursor';
            caption.appendChild(cursor);
            this.root.appendChild(caption);
        }

        this.ctx = {
            canvas: this.canvas,
            context: this.canvas.getContext('2d'),
            width: 0,
            height: 0,
            dpr: 1,
            visible: false,
            options: Object.assign({}, config.options),
            intensity: config.intensity,
            surface: config.surface,
            reducedMotion: this.reducedMotion,
            colors: () => this.colors()
        };
    }

    /** Theme colours of the layer — re-read about once a second, so a theme switch works during the animation */
    colors() {
        if (!this.colorsCache || this.frames - this.colorsCache.frame >= 24) {
            const cs = this.win.getComputedStyle(this.root);
            const c = {
                fg: cs.getPropertyValue('--dcms-mload-fg').trim() || '#737373',
                head: cs.getPropertyValue('--dcms-mload-head').trim() || '#262626',
                bg: cs.getPropertyValue('--dcms-mload-bg').trim() || '#fff',
                gain: parseFloat(cs.getPropertyValue('--dcms-mload-gain')) || 1,
                glow: parseFloat(cs.getPropertyValue('--dcms-mload-glow')) || 0
            };
            c.key = [c.fg, c.head, c.bg, c.gain, c.glow].join('|');
            this.colorsCache = { frame: this.frames, value: c };
        }
        return this.colorsCache.value;
    }

    /**
     * Visible part of the layer in its own CSS px. A list lives in a frame as tall as its content (the
     * main window scrolls), so its layer may be several screens tall — only the part on the screen is
     * painted. Walks up the same-origin frames, clipping by each viewport.
     *
     * @returns {{top: number, height: number}}
     */
    visibleBand() {
        const rect = this.root.getBoundingClientRect();
        let clipTop = 0;
        let clipBottom = this.win.innerHeight;
        let offset = 0; // y = 0 of this window in the coordinates of the ancestor being checked
        try {
            let w = this.win;
            while (w !== w.top && w.frameElement) {
                const frame = w.frameElement;
                offset += frame.getBoundingClientRect().top + frame.clientTop;
                w = w.parent;
                clipTop = Math.max(clipTop, -offset);
                clipBottom = Math.min(clipBottom, w.innerHeight - offset);
            }
        } catch (e) {
            // cross-origin ancestor — clipped by the reachable viewports only
        }
        const top = Math.max(0, Math.floor(clipTop - rect.top));
        const bottom = Math.min(Math.ceil(rect.height), Math.ceil(clipBottom - rect.top));
        return bottom > top ? { top, height: bottom - top } : { top: 0, height: 0 };
    }

    /**
     * Fits the canvas to the visible part of the layer (device pixels capped at MAX_CANVAS_PIXELS);
     * true when the effect has to know (size or pixel ratio changed). ctx.visible = anything to paint.
     */
    syncSize() {
        const w = this.root.clientWidth;
        const band = this.visibleBand();
        const h = band.height;
        if (band.top !== this.bandTop) {
            this.bandTop = band.top;
            this.canvas.style.top = band.top + 'px';
            if (this.captionEl) this.captionEl.style.top = (band.top + h / 2) + 'px';
        }
        this.ctx.visible = w > 0 && h > 0;
        if (!this.ctx.visible) return false;
        let dpr = Math.min(this.win.devicePixelRatio || 1, 2);
        if (w * h * dpr * dpr > MAX_CANVAS_PIXELS) {
            dpr = Math.max(1, Math.sqrt(MAX_CANVAS_PIXELS / (w * h)));
        }
        if (w === this.ctx.width && h === this.ctx.height && dpr === this.ctx.dpr) return false;
        this.canvas.style.height = h + 'px';
        if (this.captionEl) this.captionEl.style.top = (band.top + h / 2) + 'px';
        this.canvas.width = Math.round(w * dpr);
        this.canvas.height = Math.round(h * dpr);
        this.ctx.context.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.ctx.width = w;
        this.ctx.height = h;
        this.ctx.dpr = dpr;
        return true;
    }

    /** Runs a call of the effect; an exception stops the layer */
    guard(fn) {
        try {
            fn();
            return true;
        } catch (e) {
            console.warn('[DCMS2.modalLoader] effect "' + this.config.effect + '" failed — layer removed', e);
            this.stop();
            if (this.onError) this.onError(e);
            return false;
        }
    }

    start() {
        this.syncSize();
        if (!this.guard(() => { this.instance = this.effect.create(this.ctx) || {}; })) return;

        if (this.reducedMotion) {
            this.guard(() => {
                if (!this.ctx.visible) return;
                if (typeof this.instance.still === 'function') this.instance.still();
                else if (typeof this.instance.frame === 'function') this.instance.frame(0, 0);
            });
            if (this.decoder) this.decoder.finish();
            this.timer = this.win.setInterval(() => {
                if (this.onFrame) this.onFrame();
            }, 250);
            return;
        }

        // Between frames the page sleeps on a timer and asks for an animation frame only when the next one
        // is due — requestAnimationFrame alone would wake the page at the screen rate (60+ Hz) for 24 frames
        // a second. A hidden tab gets neither (rAF stops, timers are throttled).
        const fps = Math.min(60, Math.max(1, Number(this.effect.fps) || 24));
        const interval = 1000 / fps;
        const next = (drawnAt) => {
            const wait = Math.max(0, interval - (this.win.performance.now() - drawnAt) - 4);
            this.wake = this.win.setTimeout(() => {
                this.wake = 0;
                this.raf = this.win.requestAnimationFrame(loop);
            }, wait);
        };
        const loop = (now) => {
            this.raf = 0;
            const dt = this.last ? Math.min(now - this.last, 250) : interval;
            this.last = now;
            this.frames++;
            if (this.syncSize() && typeof this.instance.resize === 'function') {
                if (!this.guard(() => this.instance.resize())) return;
            }
            // off screen (a list scrolled away): nothing is painted, the effect does not run
            if (this.ctx.visible && typeof this.instance.frame === 'function'
                && !this.guard(() => this.instance.frame(now, dt))) return;
            if (this.decoder) this.decoder.tick(now);
            if (this.onFrame) this.onFrame();
            if (this.instance) next(now); // stop() from onFrame / an effect error ends the loop
        };
        this.raf = this.win.requestAnimationFrame(loop);
    }

    stop() {
        if (this.raf) this.win.cancelAnimationFrame(this.raf);
        if (this.wake) this.win.clearTimeout(this.wake);
        if (this.timer) this.win.clearInterval(this.timer);
        this.raf = 0;
        this.wake = 0;
        this.timer = 0;
        this.onFrame = null;
        const instance = this.instance;
        this.instance = null;
        if (instance && typeof instance.destroy === 'function') {
            try {
                instance.destroy();
            } catch (e) {
                // a failing destroy() must not keep the layer
            }
        }
    }

    remove() {
        this.stop();
        if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
    }
}

/* ── Core ───────────────────────────────────────────────────────────────── */

class DCMSModalLoader {
    constructor() {
        this.effects = new Map();
        this.sessions = new Map();
        this.overrides = {};
        this.configs = new Map();
        this.watchingWindows = false;
        this.scriptRequested = false;
    }

    register(name, effect) {
        if (typeof name !== 'string' || !NAME_PATTERN.test(name) || name === 'none' || SURFACES.includes(name)) {
            throw new TypeError('DCMS2.modalLoader.register(): invalid effect name "' + name + '"');
        }
        if (!effect || typeof effect.create !== 'function') {
            throw new TypeError('DCMS2.modalLoader.register(): effect "' + name + '" needs a create(ctx) method');
        }
        this.effects.set(name, effect);
        return this;
    }

    has(name) {
        return this.effects.has(name);
    }

    configure(options) {
        Object.assign(this.overrides, options || {});
        this.configs.clear();
        return this;
    }

    /** Raw configuration: defaults ← <html data-dcms-modal-loader> ← configure() */
    rawConfig() {
        let fromPage = {};
        try {
            const raw = document.documentElement.getAttribute('data-dcms-modal-loader');
            fromPage = raw ? JSON.parse(raw) : {};
        } catch (e) {
            console.warn('[DCMS2.modalLoader] invalid data-dcms-modal-loader — defaults used', e);
        }
        const merged = Object.assign({}, DEFAULTS, fromPage, this.overrides);
        // surfaces: merged per surface, so a page attribute without `surfaces` keeps the default list variant
        merged.surfaces = {};
        SURFACES.forEach((s) => {
            merged.surfaces[s] = Object.assign({},
                isPlainObject(DEFAULTS.surfaces[s]) ? DEFAULTS.surfaces[s] : {},
                isPlainObject(fromPage.surfaces) && isPlainObject(fromPage.surfaces[s]) ? fromPage.surfaces[s] : {},
                isPlainObject(this.overrides.surfaces) && isPlainObject(this.overrides.surfaces[s]) ? this.overrides.surfaces[s] : {});
        });
        return merged;
    }

    /**
     * Normalized configuration of a surface (window keys overridden by `surfaces[surface]`).
     *
     * @param {string} surface  modal | panel | list | page
     * @param {Object} [extra]  further overrides (preview)
     */
    getConfig(surface = 'modal', extra = null) {
        surface = SURFACES.includes(surface) ? surface : 'modal';
        if (!extra && this.configs.has(surface)) return this.configs.get(surface);
        const raw = this.rawConfig();
        const c = Object.assign({}, raw);
        SURFACE_KEYS.forEach((k) => {
            if (k in raw.surfaces[surface]) c[k] = raw.surfaces[surface][k];
        });
        if (extra) Object.assign(c, extra);
        const effect = String(c.effect || '').toLowerCase();
        const config = {
            surface,
            effect: effect === 'none' || (NAME_PATTERN.test(effect) && !SURFACES.includes(effect)) ? effect : DEFAULTS.effect,
            tone: TONES.includes(c.tone) ? c.tone : DEFAULTS.tone,
            intensity: INTENSITIES.includes(c.intensity) ? c.intensity : DEFAULTS.intensity,
            caption: CAPTIONS.includes(c.caption) ? c.caption : DEFAULTS.caption,
            background: BACKGROUNDS.includes(c.background) ? c.background : DEFAULTS.background,
            showDelayMs: clampInt(c.showDelayMs, 0, 5000, DEFAULTS.showDelayMs),
            minShowMs: clampInt(c.minShowMs, 0, 5000, DEFAULTS.minShowMs),
            maxMs: clampInt(c.maxMs, 1000, 300000, DEFAULTS.maxMs),
            script: typeof c.script === 'string' ? c.script : '',
            options: {}
        };
        // The user's appearance preferences (`user`: effect, intensity) apply where the portal has an effect —
        // a surface the portal switched off stays off; previews (extra) show the configuration as given
        const user = isPlainObject(c.user) ? c.user : {};
        if (!extra && config.effect !== 'none') {
            const userEffect = typeof user.effect === 'string' ? user.effect.toLowerCase() : 'off';
            if (userEffect === 'off') {
                config.effect = 'none';
            } else if (userEffect !== 'portal' && NAME_PATTERN.test(userEffect) && !SURFACES.includes(userEffect)) {
                config.effect = userEffect;
            }
            if (config.effect !== 'none' && user.intensity === 'subtle') {
                config.intensity = 'whisper';
                config.caption = 'none';
            }
        }
        // options of the effect: effectOptions[effect], or `options` given directly (preview)
        const allOptions = isPlainObject(c.effectOptions) ? c.effectOptions : {};
        config.options = isPlainObject(extra && extra.options) ? extra.options
            : (isPlainObject(allOptions[config.effect]) ? allOptions[config.effect] : {});
        if (!extra) this.configs.set(surface, config);
        return config;
    }

    /** Every DCMS2 document: project effect file; the top one also watches the windows */
    init() {
        this.loadProjectScript();
        if (this.watchingWindows || dcmsTopWindow() !== window) return;
        this.watchingWindows = true;
        const observer = new MutationObserver((records) => this.onMutations(records));
        observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden'] });
        document.querySelectorAll('.iziModal-content-loader').forEach((el) => this.beginContent(el));
    }

    /** Project effect file (`script` in [modalLoader]) — loaded once per document, it registers itself */
    loadProjectScript() {
        const raw = this.rawConfig();
        const script = typeof raw.script === 'string' ? raw.script : '';
        if (this.scriptRequested || !script) return;
        const wanted = [raw.effect].concat(SURFACES.map((s) => raw.surfaces[s].effect)).filter(Boolean);
        if (wanted.every((name) => name === 'none' || this.has(name))) return;
        this.scriptRequested = true;
        const tag = document.createElement('script');
        tag.src = script;
        tag.async = true;
        tag.onerror = () => console.warn('[DCMS2.modalLoader] effect script not loaded: ' + script);
        (document.head || document.documentElement).appendChild(tag);
    }

    onMutations(records) {
        for (const record of records) {
            const t = record.target;
            if (t.nodeType !== 1) continue;
            if (t.classList.contains('iziModal-content')) {
                if (t.classList.contains('iziModal-content-loader')) {
                    this.beginContent(t);
                } else if (this.sessions.has(t)) {
                    this.end(this.sessions.get(t));
                }
            } else if (record.attributeName === 'aria-hidden' && t.classList.contains('iziModal')
                && t.getAttribute('aria-hidden') === 'true') {
                const content = t.querySelector('.iziModal-content');
                if (content && this.sessions.has(content)) this.end(this.sessions.get(content));
            }
        }
    }

    /** The window of an element is closed (removed, hidden by iziModal, or the element is not rendered) */
    isClosed(el) {
        if (!el.isConnected) return true;
        const modal = el.closest('.iziModal');
        if (modal) return modal.getAttribute('aria-hidden') === 'true';
        return el.getClientRects().length === 0;
    }

    beginContent(content) {
        this.begin(content, content.parentElement, 'modal',
            () => !this.isClosed(content) && content.classList.contains('iziModal-content-loader'));
    }

    /**
     * Layer over a frame until it has loaded. The parent of the frame becomes the host.
     *
     * @param {HTMLIFrameElement} iframe
     * @param {{surface?: string, until?: string}} [options] until: 'load' (default) or 'interactive'
     */
    watchFrame(iframe, options = {}) {
        if (!iframe || !iframe.parentElement) return;
        const previous = this.sessions.get(iframe);
        if (previous) this.drop(previous); // a new page in the same frame (another row of the panel)
        let startDoc = null;
        try {
            startDoc = iframe.contentDocument;
        } catch (e) {
            // cross-origin frame — the load event decides
        }
        let loaded = false;
        const onLoad = () => {
            loaded = true;
            const s = this.sessions.get(iframe);
            if (s) this.end(s);
        };
        iframe.addEventListener('load', onLoad, { once: true });
        const parsed = () => {
            if (options.until !== 'interactive') return false;
            try {
                const doc = iframe.contentDocument;
                return !!doc && doc !== startDoc && doc.readyState !== 'loading' && doc.URL !== 'about:blank';
            } catch (e) {
                return false;
            }
        };
        const s = this.begin(iframe, iframe.parentElement, options.surface || 'modal',
            () => !loaded && !parsed() && !this.isClosed(iframe));
        if (s) s.cleanup = () => iframe.removeEventListener('load', onLoad);
    }

    /**
     * Layer in a positioned element until hide() — for loads the core cannot see (a list reloading its frame).
     *
     * @param {HTMLElement} host
     * @param {{surface?: string}} [options]
     * @returns {{hide: Function}}
     */
    show(host, options = {}) {
        if (!host) return { hide() {} };
        const previous = this.sessions.get(host);
        if (previous) this.drop(previous);
        const handle = { hidden: false };
        const s = this.begin(host, host, options.surface || 'list', () => !handle.hidden && host.isConnected);
        handle.hide = () => {
            handle.hidden = true;
            if (s && this.sessions.get(host) === s) this.end(s);
        };
        return handle;
    }

    /**
     * @param {Element} key            identity of the load (one layer per key)
     * @param {Element} host           element the layer is appended to
     * @param {string} surface         modal | panel | list | page
     * @param {Function} stillLoading  true while loading lasts
     * @returns {Object|null} session
     */
    begin(key, host, surface, stillLoading) {
        if (this.sessions.has(key) || !host) return null;
        const config = this.getConfig(surface);
        if (config.effect === 'none') return null;
        const win = host.ownerDocument.defaultView || window;
        const s = { key, host, win, config, stillLoading, layer: null, timer: 0, shownAt: 0, startedAt: Date.now(), ending: false, cleanup: null };
        this.sessions.set(key, s);
        s.timer = win.setTimeout(() => this.reveal(s), config.showDelayMs);
        return s;
    }

    reveal(s) {
        s.timer = 0;
        const effect = this.effects.get(s.config.effect);
        if (!effect || !s.stillLoading() || !s.host.isConnected) {
            if (!effect) console.warn('[DCMS2.modalLoader] unknown effect "' + s.config.effect + '"');
            this.drop(s);
            return;
        }
        if (s.win.getComputedStyle(s.host).position === 'static') {
            s.host.style.position = 'relative';
        }
        s.layer = new Layer(s.host.ownerDocument, s.config, effect, false);
        s.layer.onError = () => this.drop(s);
        s.layer.onFrame = () => {
            if (!s.stillLoading() || Date.now() - s.startedAt > s.config.maxMs) this.end(s);
        };
        s.host.appendChild(s.layer.root);
        s.layer.start();
        if (this.sessions.get(s.key) !== s) return; // the effect failed in create()
        s.shownAt = Date.now();
        void s.layer.root.offsetWidth; // the opacity transition starts from 0
        s.layer.root.classList.add('is-visible');
    }

    /** Ends a load: fade-out after minShowMs, at once when the window is gone */
    end(s) {
        if (s.ending) return;
        s.ending = true;
        if (s.timer) {
            s.win.clearTimeout(s.timer);
            s.timer = 0;
        }
        if (!s.layer || this.isClosed(s.host)) {
            this.drop(s);
            return;
        }
        const wait = Math.max(0, s.config.minShowMs - (Date.now() - s.shownAt));
        s.win.setTimeout(() => {
            if (s.layer.decoder) s.layer.decoder.finish();
            s.layer.root.classList.remove('is-visible');
            s.win.setTimeout(() => this.drop(s), FADE_OUT_MS);
        }, wait);
    }

    /** Removes a load immediately */
    drop(s) {
        s.ending = true;
        if (s.timer) {
            s.win.clearTimeout(s.timer);
            s.timer = 0;
        }
        if (s.layer) s.layer.remove();
        if (s.cleanup) s.cleanup();
        if (this.sessions.get(s.key) === s) this.sessions.delete(s.key);
    }

    /**
     * Endless layer in any positioned element — documentation and demos.
     *
     * @param {HTMLElement} host
     * @param {Object} options  configuration keys (effect, tone, intensity, caption, background, options),
     *                          surface, theme: 'light' | 'dark' to force the theme of the layer
     * @returns {{destroy: Function}}
     */
    preview(host, options = {}) {
        const extra = Object.assign({}, options);
        delete extra.surface;
        delete extra.theme;
        const config = this.getConfig(options.surface || 'modal', extra);
        const effect = this.effects.get(config.effect);
        if (!effect || !host) {
            return { destroy() {} };
        }
        const layer = new Layer(host.ownerDocument, config, effect, true);
        if (options.theme === 'light' || options.theme === 'dark') {
            layer.root.classList.add('dcms-mload--force-' + options.theme);
        }
        layer.root.classList.add('is-visible');
        host.appendChild(layer.root);
        layer.onError = () => layer.remove();
        layer.start();
        return { destroy: () => layer.remove() };
    }
}

window.DCMS2 = window.DCMS2 || {};
if (!window.DCMS2.modalLoader) {
    window.DCMS2.modalLoader = new DCMSModalLoader();
    window.DCMS2.modalLoader.register('rain', rainEffect);
    window.DCMS2.modalLoader.init();
}

export { DCMSModalLoader };
